import { Globe, MapPin, Layers } from 'lucide-react';
import type { Portfolio } from '../hooks/usePortfolios';
import { useAuthStore } from '../store/authStore';
import { ROLES } from '../constants/roles';

interface PortfolioSummaryCardProps {
  portfolio: Portfolio;
  onClick?: (portfolioId: string) => void;
  isSelected?: boolean;
}

const getRegionIcon = (name: string) => {
  if (name.toLowerCase().includes('north america') || name.toLowerCase().includes('america')) return Globe;
  if (name.toLowerCase().includes('europe')) return MapPin;
  if (name.toLowerCase().includes('asia') || name.toLowerCase().includes('pacific')) return Layers;
  return Globe;
};

export function PortfolioSummaryCard({ portfolio, onClick, isSelected = false }: PortfolioSummaryCardProps) {
  const toNum = (val: number | string): number => typeof val === 'number' ? val : parseInt(val as string, 10);
  const user = useAuthStore((s) => s.user);
  const RegionIcon = getRegionIcon(portfolio.name);
  const onlineCount = toNum(portfolio.sites_online);
  const totalSites = toNum(portfolio.total_sites);

  // Role-gated card heading. Cross-tenant ops roles (platform_admin = Djin
  // internal, parent_org_admin = POD-tier across customers) see the literal
  // portfolio name — they don't have a single customer brand to surface. All
  // customer-tier roles (customer_admin through site_viewer) are scoped to
  // their home customer's sites by auth.get_user_accessible_sites (migration
  // 040 made customer_admin honour user_site_access; the bypass-default that
  // used to motivate showing customer_admin the literal portfolio name is
  // gone), so their customer's name is the right brand to surface. Users
  // without customer_name (transitional state or orphan customer_id) fall
  // through to the literal portfolio name.
  const showLiteralName =
    user?.role === ROLES.PLATFORM_ADMIN ||
    user?.role === ROLES.PARENT_ORG_ADMIN ||
    !user?.customer_name;
  const displayName = showLiteralName ? portfolio.name : user!.customer_name!;

  return (
    <div
      onClick={() => onClick?.(portfolio.portfolio_id)}
      className={`cursor-pointer rounded-2xl p-4 transition-all border ${
        isSelected
          ? 'bg-ems-green/10 border-ems-green/20'
          : 'bg-ems-bg-card border-ems-border hover:bg-ems-bg-card-hover'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-ems-green/10' : 'bg-ems-bg'}`}>
          <RegionIcon className={`w-4 h-4 ${isSelected ? 'text-ems-green' : 'text-ems-text-muted'}`} />
        </div>
        {isSelected && (
          <span className="text-[10px] bg-ems-green/10 text-ems-green px-1.5 py-0.5 rounded font-medium">Active</span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-ems-text-primary mb-1">{displayName}</h3>
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-bold text-ems-text-primary font-mono">{totalSites}</span>
          <span className="text-xs text-ems-text-muted">sites</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${onlineCount === totalSites ? 'bg-status-online' : 'bg-status-degraded'}`} />
          <span className={`text-sm font-medium ${onlineCount === totalSites ? 'text-status-online' : 'text-status-degraded'}`}>
            <span className="font-mono">{onlineCount}/{totalSites}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
